import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, UserPlus, LogOut, ShoppingCart, LayoutDashboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { storage, userApi, cartStorage, cartApi, tokenStorage } from '@/user/api';
import logoMini from '../assest/imgs/kmzs-logo-mini.png';

const navItems = [
  { label: '首页', href: '/' },
  { label: '资讯中心', href: '/articles' },
  // { label: '营销中心', href: '/marketing' },
  // { label: '资源中心', href: '/resources' },
  { label: '在线商城', href: '/shop' },
  // { label: '分销中心', href: '/distribution' },
  // { label: '招商中心', href: '/investment' },
  // { label: '新疆平安', href: '/pingan' },
  { label: '票据平台', href: 'https://zxkf-web.aero-credit.com/login' },
  { label: '采购平台', href: 'https://swueecg.com/#/enterpriseZoneDetail?id=2049001811704049665&isQingxin=' },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(storage.getUserInfo());
  const [cartCount, setCartCount] = useState(cartStorage.getCartCount());
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 监听登录状态变化
  useEffect(() => {
    const checkLogin = () => {
      setUserInfo(storage.getCurrentUser());
    };
    
    // 每秒检查一次登录状态
    const interval = setInterval(checkLogin, 1000);
    return () => clearInterval(interval);
  }, []);

  // 监听购物车数量变化
  useEffect(() => {
    const checkCart = async () => {
      if (tokenStorage.isLoggedIn()) {
        try {
          const res = await cartApi.getCartList();
          if (res.code === 200 && res.data) {
            const count = res.data.reduce((sum, item) => sum + item.quantity, 0);
            setCartCount(count);
            return;
          }
        } catch {
          // fallback to local
        }
      }
      setCartCount(cartStorage.getCartCount());
    };
    checkCart();
    const interval = setInterval(checkCart, 3000);
    return () => clearInterval(interval);
  }, []);

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const handleLogout = async () => {
    try {
      await userApi.logout();
      alert('退出登录成功');
      navigate('/');
    } catch {
      alert('退出登录失败');
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-sm py-3'
          : 'bg-white py-5'
      }`}
      style={{
        transitionTimingFunction: 'var(--ease-expo-out)',
      }}
    >
      <div className="section-container">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group"
          >
            <div className="h-10 bg-[#1c5bff] rounded-lg flex">
              <img src={logoMini} alt="孔明logo" className="h-full w-auto" />
            </div>
            <span className="text-xl font-bold text-black hidden sm:block">
              互联网生态平台
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item, index) => (
              <Link
                key={item.href}
                to={item.href}
                className={`relative px-3 py-2 text-sm font-medium transition-all duration-300 rounded-lg group ${
                  isActive(item.href)
                    ? 'text-[#1c5bff]'
                    : 'text-gray-600 hover:text-black'
                }`}
                style={{
                  animationDelay: `${index * 100}ms`,
                }}
              >
                {item.label}
                <span
                  className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-[#1c5bff] transition-all duration-300 ${
                    isActive(item.href)
                      ? 'w-full'
                      : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* Action Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/cart"
              className="relative p-2 text-gray-600 hover:text-[#1c5bff] hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center bg-red-500 text-white text-[10px] rounded-full">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Link>
            {userInfo ? (
              <>
                <span className="text-sm text-gray-600">
                  欢迎，{userInfo.username}
                </span>
                <Link
                  to="/console"
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-[#1c5bff] hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  控制台
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-gray-600 hover:text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  退出
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/login')}
                  className="text-gray-600 hover:text-black hover:bg-gray-100"
                >
                  <User className="w-4 h-4 mr-2" />
                  登录
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-500 ${
            isMobileMenuOpen ? 'max-h-[500px] mt-4' : 'max-h-0'
          }`}
          style={{
            transitionTimingFunction: 'var(--ease-expo-out)',
          }}
        >
          <nav className="flex flex-col gap-2 pb-4">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                  isActive(item.href)
                    ? 'bg-[#1c5bff] text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-black'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Mobile User Actions */}
            <div className="mt-2 pt-2 border-t border-gray-100">
              {userInfo ? (
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm text-gray-600">
                    {userInfo.username}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    退出
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      navigate('/login');
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <User className="w-4 h-4 mr-2" />
                    登录
                  </Button>
                  <Button
                    className="flex-1 bg-[#1c5bff] text-white hover:bg-[#1547cc]"
                    onClick={() => {
                      navigate('/register');
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    注册
                  </Button>
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}
